import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Box, Button, Chip, IconButton, Stack, Tab, Tabs, TextField, Tooltip, Typography, MenuItem, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel, Slider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/PersonAddAlt";
import VerifiedIcon from "@mui/icons-material/Verified";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GridColDef } from "@mui/x-data-grid";
import { ConfirmDialog, ServerDataGrid, formatCnpj, formatDate, useDataGridState } from "@safrater/ui";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";
import { AssociateMemberDialog } from "./AssociateMemberDialog";
import { AccessDrawer } from "../users/AccessDrawer";
import { GrantLicenseDialog } from "../licenses/LicensesPage";

const MEMBER_STATUS_COLORS: Record<string, "success" | "warning" | "error" | "default"> = {
  ativo: "success", pendente: "warning", suspenso: "error",
};

interface InstitutionDetail {
  id: string; name: string; cnpj: string; status: string; email: string | null; phone: string | null;
}

export function InstitutionDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState(0);

  const isMaster = !!user?.isMasterAdmin;
  const canManageMembers = isMaster || (user?.adminInstitutionIds ?? []).includes(id);

  const { data: inst } = useQuery({
    queryKey: ["institution", id],
    queryFn: async () => (await api.get<InstitutionDetail>(`/institutions/${id}`)).data,
    enabled: !!id,
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton onClick={() => navigate("/institutions")} aria-label="Voltar"><ArrowBackIcon /></IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>{inst?.name ?? "Instituição"}</Typography>
        {inst && <Chip size="small" label={inst.status} color={inst.status === "active" ? "success" : inst.status === "suspended" ? "error" : "warning"} />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {inst ? formatCnpj(inst.cnpj) : ""}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab label="Dados" />
        <Tab label="Usuários" />
        <Tab label="Licenças" />
        <Tab label="Fundo" />
      </Tabs>

      {tab === 0 && inst && <DadosTab inst={inst} />}
      {tab === 1 && <MembersTab institutionId={id} canManage={canManageMembers} />}
      {tab === 2 && <LicensesTab institutionId={id} institutionName={inst?.name ?? ""} canManage={isMaster} />}
      {tab === 3 && <BrandingTab institutionId={id} canManage={canManageMembers} />}
    </Box>
  );
}

function DadosTab({ inst }: { inst: InstitutionDetail }) {
  return (
    <Stack spacing={1} sx={{ maxWidth: 480 }}>
      <Row label="Nome" value={inst.name} />
      <Row label="CNPJ" value={formatCnpj(inst.cnpj)} />
      <Row label="E-mail" value={inst.email ?? "—"} />
      <Row label="Telefone" value={inst.phone ?? "—"} />
      <Row label="Status" value={inst.status} />
    </Stack>
  );
}
function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 120 }}>{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}

interface MemberRow {
  id: string;
  kind: "member" | "invite";
  membershipId?: string;
  inviteId?: string;
  userId?: string;
  name: string;
  email: string;
  status: "ativo" | "pendente" | "suspenso";
  isAdmin: boolean;
  roles: { id: string; key: string; name: string; module: { key: string } }[];
}

function MembersTab({ institutionId, canManage }: { institutionId: string; canManage: boolean }) {
  const state = useDataGridState();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [associateOpen, setAssociateOpen] = useState(false);
  const [accessFor, setAccessFor] = useState<{ userId: string; name: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<MemberRow | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["institution-members", institutionId, state.queryParams],
    queryFn: async () => (await api.get<{ data: MemberRow[]; total: number }>(`/institutions/${institutionId}/members`, { params: state.queryParams })).data,
    placeholderData: (prev) => prev,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["institution-members", institutionId] });

  const removeMemberM = useMutation({
    mutationFn: (membershipId: string) => api.delete(`/institutions/${institutionId}/members/${membershipId}`),
    onSuccess: () => { invalidate(); setConfirmRemove(null); },
  });
  const revokeInviteM = useMutation({
    mutationFn: (inviteId: string) => api.delete(`/institutions/${institutionId}/invites/${inviteId}`),
    onSuccess: () => { invalidate(); setConfirmRemove(null); },
  });
  const resendInviteM = useMutation({
    mutationFn: (inviteId: string) => api.post(`/institutions/${institutionId}/invites/${inviteId}/resend`),
    onSuccess: invalidate,
  });

  const columns: GridColDef<MemberRow>[] = [
    { field: "name", headerName: "Nome", flex: 1, minWidth: 200 },
    { field: "email", headerName: "E-mail", flex: 1, minWidth: 220 },
    {
      field: "status", headerName: "Status", width: 120,
      renderCell: ({ value }) => <Chip size="small" label={value as string} color={MEMBER_STATUS_COLORS[value as string] ?? "default"} />,
    },
    {
      field: "isAdmin", headerName: "Admin", width: 90, sortable: false,
      renderCell: ({ value }) => (value ? <Chip size="small" color="info" label="admin" /> : null),
    },
    {
      field: "roles", headerName: "Papéis", flex: 1, minWidth: 220, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" flexWrap="wrap" spacing={0.5}>
          {row.roles.slice(0, 4).map((r) => <Chip key={r.id} size="small" variant="outlined" label={`${r.module.key}/${r.name}`} />)}
          {row.roles.length > 4 && <Chip size="small" label={`+${row.roles.length - 4}`} />}
        </Stack>
      ),
    },
    ...(canManage ? [{
      field: "_actions", headerName: "Ações", width: 150, sortable: false, filterable: false,
      renderCell: ({ row }: { row: MemberRow }) => (
        <Stack direction="row" spacing={0.5}>
          {row.kind === "member" && row.userId && (
            <Tooltip title="Gerir papéis"><IconButton size="small" onClick={() => setAccessFor({ userId: row.userId!, name: row.name })}><ManageAccountsIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {row.kind === "invite" && row.inviteId && (
            <Tooltip title="Reenviar convite"><IconButton size="small" onClick={() => resendInviteM.mutate(row.inviteId!)}><SendIcon fontSize="small" /></IconButton></Tooltip>
          )}
          <Tooltip title={row.kind === "invite" ? "Revogar convite" : "Remover da instituição"}>
            <IconButton size="small" color="error" onClick={() => setConfirmRemove(row)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      ),
    } as GridColDef<MemberRow>] : []),
  ];

  return (
    <Box>
      {canManage && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAssociateOpen(true)}>Associar usuário</Button>
        </Stack>
      )}
      <Box sx={{ height: 560 }}>
        <ServerDataGrid<MemberRow>
          gridId="central.institution.members"
          userScope={user?.sub}
          rows={data?.data ?? []} rowCount={data?.total ?? 0} columns={columns} loading={isFetching}
          paginationModel={state.pagination} onPaginationModelChange={state.setPagination}
          sortModel={state.sort} onSortModelChange={state.setSort}
          filterModel={state.filter} onFilterModelChange={state.setFilter}
        />
      </Box>

      {associateOpen && (
        <AssociateMemberDialog
          institutionId={institutionId}
          onClose={() => setAssociateOpen(false)}
          onDone={() => { setAssociateOpen(false); invalidate(); }}
        />
      )}

      <AccessDrawer open={!!accessFor} userId={accessFor?.userId ?? null} userName={accessFor?.name}
        onClose={() => { setAccessFor(null); invalidate(); }} />

      <ConfirmDialog
        open={!!confirmRemove}
        title={confirmRemove?.kind === "invite" ? "Revogar convite" : "Remover usuário"}
        description={confirmRemove?.kind === "invite"
          ? `Revogar o convite de ${confirmRemove?.email}?`
          : `Remover ${confirmRemove?.name} desta instituição? O acesso será desativado.`}
        confirmLabel={confirmRemove?.kind === "invite" ? "Revogar" : "Remover"}
        severity="error"
        loading={removeMemberM.isPending || revokeInviteM.isPending}
        onConfirm={() => {
          if (!confirmRemove) return;
          if (confirmRemove.kind === "invite" && confirmRemove.inviteId) revokeInviteM.mutate(confirmRemove.inviteId);
          else if (confirmRemove.membershipId) removeMemberM.mutate(confirmRemove.membershipId);
        }}
        onCancel={() => setConfirmRemove(null)}
      />
    </Box>
  );
}

interface LicenseRow {
  id: string; status: string; validUntil: string | null;
  module: { id: string; name: string; key: string };
  licenseType: { id: string; kind: string; name: string };
}

interface BrandingView {
  moduleKey: string; fileHash: string; fit: string; position: string; repeat: boolean; opacity: number; updatedAt: string;
}

const FIT_OPTIONS: { value: string; label: string }[] = [
  { value: "cover", label: "Cobrir (cover)" },
  { value: "contain", label: "Conter (contain)" },
  { value: "full", label: "Esticar (100% x 100%)" },
  { value: "auto", label: "Tamanho original (auto)" },
];
const POSITION_OPTIONS: { value: string; label: string }[] = [
  { value: "center", label: "Centro" },
  { value: "top", label: "Topo" },
  { value: "bottom", label: "Base" },
  { value: "left", label: "Esquerda" },
  { value: "right", label: "Direita" },
  { value: "top left", label: "Superior esquerdo" },
  { value: "top right", label: "Superior direito" },
  { value: "bottom left", label: "Inferior esquerdo" },
  { value: "bottom right", label: "Inferior direito" },
];

/** Edição da imagem de fundo (branding) por aplicação/módulo da instituição. */
function BrandingTab({ institutionId, canManage }: { institutionId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ moduleKey: string; moduleName: string } | null>(null);

  // Módulos licenciados + a própria Central (que também tem AppShell).
  const { data: licenses } = useQuery({
    queryKey: ["institution-licenses-all", institutionId],
    queryFn: async () =>
      (await api.get<{ data: LicenseRow[] }>("/licenses", { params: { institution_id: institutionId, page_size: 100 } })).data,
  });
  const modules = [
    { key: "central", name: "Central de Acessos" },
    ...(licenses?.data ?? []).map((l) => ({ key: l.module.key, name: l.module.name })),
  ];

  const { data: brandings } = useQuery({
    queryKey: ["institution-branding", institutionId],
    queryFn: async () => (await api.get<BrandingView[]>(`/institutions/${institutionId}/branding`)).data,
  });
  const byModule = new Map((brandings ?? []).map((b) => [b.moduleKey, b]));
  const invalidate = () => qc.invalidateQueries({ queryKey: ["institution-branding", institutionId] });

  const removeM = useMutation({
    mutationFn: (moduleKey: string) => api.delete(`/institutions/${institutionId}/branding/${moduleKey}`),
    onSuccess: invalidate,
  });

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {canManage
          ? "Defina a imagem de fundo de cada aplicação para esta instituição. Sem customização, a aplicação usa a imagem padrão."
          : "Imagens de fundo por aplicação (somente leitura)."}
      </Typography>
      <Stack spacing={1}>
        {modules.map((m) => {
          const b = byModule.get(m.key);
          return (
            <Stack key={m.key} direction="row" alignItems="center" spacing={2}
              sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {b ? `Personalizada — ${b.fit}, ${b.position}, ${b.opacity}%` : "Imagem padrão"}
                </Typography>
              </Box>
              {canManage && (
                <>
                  <Button size="small" onClick={() => setEditing({ moduleKey: m.key, moduleName: m.name })}>
                    {b ? "Alterar" : "Definir"}
                  </Button>
                  {b && (
                    <Button size="small" color="error" disabled={removeM.isPending}
                      onClick={() => removeM.mutate(m.key)}>
                      Remover
                    </Button>
                  )}
                </>
              )}
            </Stack>
          );
        })}
      </Stack>

      {editing && (
        <BrandingEditorDialog
          institutionId={institutionId}
          moduleKey={editing.moduleKey}
          moduleName={editing.moduleName}
          current={byModule.get(editing.moduleKey) ?? null}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); invalidate(); }}
        />
      )}
    </Box>
  );
}

function BrandingEditorDialog({
  institutionId, moduleKey, moduleName, current, onClose, onDone,
}: {
  institutionId: string; moduleKey: string; moduleName: string;
  current: BrandingView | null; onClose: () => void; onDone: () => void;
}) {
  const [source, setSource] = useState<"url" | "file">("url");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fit, setFit] = useState(current?.fit ?? "cover");
  const [position, setPosition] = useState(current?.position ?? "center");
  const [repeat, setRepeat] = useState<boolean>(current?.repeat ?? false);
  // Opacidade (%) da imagem: 60 por padrão em branding novo (fundo suave);
  // ao editar um existente, carrega o valor salvo.
  const [opacity, setOpacity] = useState<number>(current?.opacity ?? 60);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // O arquivo pertence ao tenant da INSTITUIÇÃO editada (não ao tenant atual do
  // ator) — garante que o satélite dessa instituição consiga ler a imagem.
  const tq = `?tenantId=${institutionId}`;

  const saveM = useMutation({
    mutationFn: async () => {
      let fileHash: string;
      if (source === "file") {
        if (!file) throw new Error("Selecione um arquivo");
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post<{ hash: string }>(`/files/v1/files${tq}`, fd);
        fileHash = up.data.hash;
      } else {
        const up = await api.post<{ hash: string }>(`/files/v1/files/from-url${tq}`, { url: url.trim() });
        fileHash = up.data.hash;
      }
      await api.put(`/institutions/${institutionId}/branding/${moduleKey}`, { fileHash, fit, position, repeat, opacity });
    },
    onSuccess: onDone,
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(", ") : (msg ?? (e as Error)?.message) ?? "Falha ao salvar a imagem de fundo.");
    },
  });

  const resetM = useMutation({
    mutationFn: () => api.delete(`/institutions/${institutionId}/branding/${moduleKey}`),
    onSuccess: onDone,
    onError: () => setError("Falha ao restaurar o padrão."),
  });

  const canSave = source === "url" ? !!url.trim() : !!file;
  const busy = saveM.isPending || resetM.isPending;

  return (
    <Box role="dialog" aria-modal
      sx={{ position: "fixed", inset: 0, bgcolor: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", zIndex: (t) => t.zIndex.modal }}>
      <Box sx={{ bgcolor: "background.paper", p: 3, borderRadius: 2, width: 480, maxWidth: "90vw" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Imagem de fundo — {moduleName}</Typography>
        <Stack spacing={2}>
          <ToggleButtonGroup
            size="small" exclusive value={source}
            onChange={(_, v) => { if (v) { setSource(v); setError(null); } }}
            aria-label="Origem da imagem"
          >
            <ToggleButton value="url">Por URL</ToggleButton>
            <ToggleButton value="file">Do computador</ToggleButton>
          </ToggleButtonGroup>

          {source === "url" ? (
            <TextField
              label="URL da imagem" placeholder="https://…/imagem.jpg" value={url}
              onChange={(e) => setUrl(e.target.value)} fullWidth autoFocus
              helperText="A imagem será baixada e armazenada no file-service."
            />
          ) : (
            <Box>
              <input
                ref={fileRef} type="file" accept="image/*" hidden
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <Button variant="outlined" onClick={() => fileRef.current?.click()}>
                Escolher arquivo…
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {file ? file.name : "Nenhum arquivo selecionado"}
              </Typography>
            </Box>
          )}

          <TextField select label="Disposição" value={fit} onChange={(e) => setFit(e.target.value)} fullWidth>
            {FIT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField select label="Alinhamento" value={position} onChange={(e) => setPosition(e.target.value)} fullWidth>
            {POSITION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Checkbox checked={repeat} onChange={(e) => setRepeat(e.target.checked)} />}
            label="Repetir (imagens de padrão/textura)"
          />
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Opacidade da imagem: <b>{opacity}%</b>
            </Typography>
            <Slider
              value={opacity}
              onChange={(_, v) => setOpacity(Array.isArray(v) ? v[0] : v)}
              min={0} max={100} step={5}
              marks={[{ value: 0, label: "0%" }, { value: 60, label: "60%" }, { value: 100, label: "100%" }]}
              valueLabelDisplay="auto"
              aria-label="Opacidade da imagem de fundo"
            />
            <Typography variant="caption" color="text.secondary">
              100% = imagem cheia; valores menores deixam a imagem mais suave (fundo do tema aparece por cima).
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1} alignItems="center">
            {current && (
              <Button color="warning" onClick={() => { setError(null); resetM.mutate(); }} disabled={busy}>
                Restaurar padrão
              </Button>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={onClose} disabled={busy}>Cancelar</Button>
            <Button variant="contained" onClick={() => { setError(null); saveM.mutate(); }}
              disabled={busy || !canSave}
              startIcon={saveM.isPending ? <CircularProgress size={16} /> : undefined}>
              Salvar
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

function LicensesTab({ institutionId, institutionName, canManage }: { institutionId: string; institutionName: string; canManage: boolean }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const state = useDataGridState();
  const [grantOpen, setGrantOpen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<LicenseRow | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["institution-licenses", institutionId, state.queryParams],
    queryFn: async () => (await api.get<{ data: LicenseRow[]; total: number }>("/licenses", { params: { ...state.queryParams, institution_id: institutionId } })).data,
    placeholderData: (prev) => prev,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["institution-licenses", institutionId] });

  const grantM = useMutation({
    mutationFn: (b: { institutionId: string; moduleId: string; licenseTypeId: string }) => api.post("/licenses/grant", b),
    onSuccess: () => { invalidate(); setGrantOpen(false); },
  });
  const revokeM = useMutation({
    mutationFn: (licenseId: string) => api.post(`/licenses/${licenseId}/revoke`),
    onSuccess: () => { invalidate(); setConfirmRevoke(null); },
  });

  const columns: GridColDef<LicenseRow>[] = [
    { field: "module", headerName: "Módulo", flex: 1, minWidth: 180, sortable: false, valueGetter: (_v, row) => row.module.name },
    { field: "licenseType", headerName: "Tipo", width: 180, sortable: false, valueGetter: (_v, row) => row.licenseType.name },
    { field: "validUntil", headerName: "Válida até", width: 140, valueFormatter: (v) => (v ? formatDate(v as string) : "—") },
    {
      field: "status", headerName: "Status", width: 120,
      renderCell: ({ value }) => <Chip size="small" label={value as string} color={value === "active" ? "success" : value === "expired" ? "error" : "warning"} />,
    },
    ...(canManage ? [{
      field: "_actions", headerName: "Ações", width: 100, sortable: false, filterable: false,
      renderCell: ({ row }: { row: LicenseRow }) => (row.status === "active"
        ? <Tooltip title="Revogar"><IconButton size="small" color="error" onClick={() => setConfirmRevoke(row)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        : null),
    } as GridColDef<LicenseRow>] : []),
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {canManage ? "Conceda ou revogue licenças de produtos para esta instituição." : "Licenças de produtos desta instituição (somente leitura)."}
        </Typography>
        {canManage && <Button variant="contained" startIcon={<VerifiedIcon />} onClick={() => setGrantOpen(true)}>Adicionar licença</Button>}
      </Stack>
      <Box sx={{ height: 520 }}>
        <ServerDataGrid<LicenseRow>
          gridId="central.institution.licenses"
          userScope={user?.sub}
          rows={data?.data ?? []} rowCount={data?.total ?? 0} columns={columns} loading={isFetching}
          paginationModel={state.pagination} onPaginationModelChange={state.setPagination}
          sortModel={state.sort} onSortModelChange={state.setSort}
          filterModel={state.filter} onFilterModelChange={state.setFilter}
        />
      </Box>

      {grantOpen && (
        <GrantLicenseDialog
          lockedInstitutionId={institutionId}
          onClose={() => setGrantOpen(false)}
          onSubmit={(b) => grantM.mutate(b)}
          submitting={grantM.isPending}
        />
      )}
      <ConfirmDialog
        open={!!confirmRevoke}
        title="Revogar licença"
        description={`Revogar a licença de "${institutionName}" para "${confirmRevoke?.module.name}"?`}
        confirmLabel="Revogar" severity="error" loading={revokeM.isPending}
        onConfirm={() => { if (confirmRevoke) revokeM.mutate(confirmRevoke.id); }}
        onCancel={() => setConfirmRevoke(null)}
      />
    </Box>
  );
}
